import React, { useState, useEffect, useContext, useMemo, useRef } from 'react'
import fuzzysort from 'fuzzysort'
import { Container, Row, Col, Dropdown, DropdownToggle, DropdownItem, DropdownMenu, FormGroup, Label, Input } from "reactstrap"
import { Link, useNavigate, useParams, useSearchParams } from "react-router-dom" // using useParams for React Router v6 compatibility
import CommsConversation from "./CommsConversation"
import { collection, limit, onSnapshot, orderBy, query, where } from "firebase/firestore"
import { firestoreDb } from "../Components/firebase"
import { useApolloClient, useQuery } from "@apollo/client"
import "./CommsContainer.css"
import { MyInboxesQuery, GET_INBOX_MEMBERS } from './Queries'
import { CurrentUserContext } from '../Context/CurrentUserContext'
import moment from 'moment'
import CommsNewConversationModal from './CommsNewConversationModal'
import { AllUsersPresenceProvider } from '../Context/AllUsersPresenceProvider'
import Select from 'react-select'
import AsyncSelect from 'react-select/async'
import Loader from '../Material/Loader'

// TODO:
// [ ] - Add pagination for conversations if more than X
// [x] - Add filter for assigned to me
// [ ] - Sort by assigned to me first
// [ ] - Sort by last message date
// [ ] - Assigning con agents should show agent online status from presence
// [x] - New conversation page
// [ ] - When new message is created, redirect to that conversation if not already there
// [ ] - In admin when saving new channel, if twilio, update the twilio webhook to point to our messaging webhook with twilio api
// [ ] - Move unread sidbar count to context and update globally (with firebase listener?)

const COMMS_CONVERSATIONS_COLLECTION_NAME = "messaging_conversation"

export default function CommsContainer({ ...props }) {
    const navigate = useNavigate()
    const apolloClient = useApolloClient()
    const { currentUser } = useContext(CurrentUserContext)

    const { data, loading: loadingInboxes, error: errorInboxes, refetch, variables } = useQuery(
        MyInboxesQuery,
        {
            fetchPolicy: 'network-only',
        }
    )
    // All inboxes the user has access to
    const allInboxes = data?.myInboxes?.edges?.map(edge => edge.node) || []
    const allInboxesIds = allInboxes?.map(inbox => inbox.id) || [] // Inbox graphene IDs
    const allInboxesIdsKey = useMemo(() => allInboxesIds.join(","), [allInboxesIds]);

    const { data: inboxMembersData } = useQuery(GET_INBOX_MEMBERS, {
        variables: { inboxIds: allInboxesIds, first: 100 },
        skip: allInboxesIds.length === 0,
        fetchPolicy: 'cache-first',
    })
    const assignableReps = useMemo(() => (
        (inboxMembersData?.inboxAccessUsers || [])
            .filter(node => node?.user)
            .map(node => node.user)
    ), [inboxMembersData])

    const formatRepName = (user) => [user.firstName, user.lastName].filter(Boolean).join(' ').trim() || user.username

    // Search reps beyond the default first-100 list (see GET_INBOX_MEMBERS' searchTerm).
    const loadAssigneeOptions = async (inputValue) => {
        if (allInboxesIds.length === 0) return []
        const { data } = await apolloClient.query({
            query: GET_INBOX_MEMBERS,
            variables: { inboxIds: allInboxesIds, first: 100, searchTerm: inputValue || null },
            fetchPolicy: 'network-only',
        })
        const repOptions = (data?.inboxAccessUsers || [])
            .map(node => node?.user)
            .filter(Boolean)
            .map(rep => ({ value: rep.id, label: formatRepName(rep) }))
        return [{ value: ASSIGNEE_UNASSIGNED, label: "Unassigned" }, ...repOptions]
    }

    const { conversationId } = useParams()
    const [conversations, setConversations] = useState([])
    const [loadedStatus, setLoadedStatus] = useState(null)
    const [loading, setLoading] = useState(true)
    const [error, setError] = useState(null)
    const [searchParams, setSearchParams] = useSearchParams()
    const [inboxOpen, setInboxOpen] = useState(false);
    const [statusOpen, setStatusOpen] = useState(false);
    const [channelOpen, setChannelOpen] = useState(false);
    const STATUSES = ["All", "Open", "Snoozed", "Archived"]
    const ASSIGNEE_UNASSIGNED = "unassigned"
    const CHANNEL_TYPE_LABELS = {
        "email": "Email",
        "mobile_app_chat": "Mobile App Chat",
        "twilio_sms": "SMS",
        "twilio_whatsapp": "WhatsApp",
    }

    const selectedStatus = searchParams.get("status") || "Open"
    const assigneeParam = searchParams.get("assignee") || ""
    const selectedAssigneeIds = assigneeParam.split(",").filter(Boolean)
    const assigneeOptions = useMemo(() => {
        const repOptions = assignableReps.map(rep => ({ value: rep.id, label: formatRepName(rep) }))
        return [{ value: ASSIGNEE_UNASSIGNED, label: "Unassigned" }, ...repOptions]
    }, [assignableReps])
    // Remembers labels for reps picked via live search, since loadOptions results aren't stored elsewhere.
    const [selectedAssigneeLabels, setSelectedAssigneeLabels] = useState({})
    const groupParam = searchParams.get("groups") || ""
    const selectedGroups = groupParam.split(",").filter(Boolean)
    // Assignee -> their department groups, so conversations can be filtered by the assignee's group.
    const groupsByAssigneeId = useMemo(() => {
        const byId = {}
        assignableReps.forEach(user => {
            if (user?.id) byId[user.id] = user.departmentGroupNames || []
        })
        return byId
    }, [assignableReps])
    const groupOptions = useMemo(() => (
        [...new Set(Object.values(groupsByAssigneeId).flat())]
            .sort((a, b) => a.localeCompare(b))
            .map(name => ({ value: name, label: name }))
    ), [groupsByAssigneeId])
    const searchQuery = searchParams.get("search") || ""
    const selectedInboxId = searchParams.get("inbox") || null
    const selectedInbox = selectedInboxId ? allInboxes.find(inbox => inbox.id === selectedInboxId) || null : null
    const selectedChannelType = searchParams.get("channel_type") || null
    const unrespondedOnly = searchParams.get("unresponded") === "1"

    const updateFilter = (key, value) => {
        setSearchParams(prev => {
            const next = new URLSearchParams(prev)
            if (value) {
                next.set(key, value)
            } else {
                next.delete(key)
            }
            return next
        }, { replace: true })
    }
    const [showNewModal, setShowNewModal] = useState(false)
    const currentUserPk = currentUser?.pk ?? null
    const currentUserRepId = currentUser?.id && assignableReps.some(rep => rep.id === currentUser.id) ? currentUser.id : null

    const didBaselineFilters = useRef(false)
    const skipNextJump = useRef(false)
    const [pendingJump, setPendingJump] = useState(false)

    // Preselect the current user as the default assignee, once, on first load.
    const didInitAssignee = useRef(false)
    useEffect(() => {
        if (didInitAssignee.current || !currentUser || assignableReps.length === 0) return
        didInitAssignee.current = true
        if (searchParams.get("assignee") === null && currentUserRepId) {
            skipNextJump.current = true // a default, not a filter change the user made
            updateFilter("assignee", currentUserRepId)
        }
    }, [currentUser, assignableReps, currentUserRepId])

    useEffect(() => {
    // don’t start listening until inboxes are loaded
    if (loadingInboxes) return;

    if (allInboxesIds.length === 0) {
        setConversations([]);
        setLoadedStatus(selectedStatus);
        setLoading(false);
        return;
    }

    setLoading(true);

    let q = query(
        collection(firestoreDb, COMMS_CONVERSATIONS_COLLECTION_NAME),
        where("inbox_graphene_ids", "array-contains-any", allInboxesIds),
        orderBy("last_message_at", "desc"),
        limit(1000)
    );

    if (selectedStatus && selectedStatus !== "All") {
        q = query(
        collection(firestoreDb, COMMS_CONVERSATIONS_COLLECTION_NAME),
        where("inbox_graphene_ids", "array-contains-any", allInboxesIds),
        where("status", "==", selectedStatus === "Archived" ? "CLOSED" : selectedStatus.toUpperCase()),
        orderBy("last_message_at", "desc"),
        limit(1000)
        );
    }

    const unsubscribe = onSnapshot(
        q,
        (snapshot) => {
        const fetched = snapshot.docs.map((doc) => doc.data());
        setConversations(fetched);
        setLoadedStatus(selectedStatus);
        setLoading(false);
        setError(null);
        },
        (err) => {
        console.error("Error fetching conversations:", err);
        setError("Error fetching conversations");
        setLoading(false);
        }
    );

    // Cleanup subscription on unmount or when dependencies change
    return () => unsubscribe();
    }, [loadingInboxes, allInboxesIdsKey, selectedStatus]);

    // Filter conversations by selected inbox, fuzzy search, assignment, and channel type
    const filteredConversations = useMemo(() => {
        let candidates = conversations || []
        if (allInboxesIds.length === 0) {
            return candidates;
        }                    
        
        if (searchQuery) {
            const results = fuzzysort.go(searchQuery, conversations, {
                keys: ['subject', 'contact_name', 'contact_email', 'contact_phone', 'driver_name', 'message_subject_sliding_window'],
                threshold: -10000,
            })
            candidates = results.map(r => r.obj)
        }

        return candidates.filter(conversation => {
            const matchesInbox = !selectedInbox
                || (conversation.inbox_graphene_ids && conversation.inbox_graphene_ids.includes(selectedInbox.id))

            const matchesAssignment = selectedAssigneeIds.length === 0
                || selectedAssigneeIds.includes(conversation.assigned_to_graphene_id)
                || (selectedAssigneeIds.includes(ASSIGNEE_UNASSIGNED) && !conversation.assigned_to_graphene_id)

            const matchesChannelType = !selectedChannelType || conversation.channel_type === selectedChannelType

            const matchesUnresponded = !unrespondedOnly || conversation.last_message_is_inbound === true

            const assigneeGroups = groupsByAssigneeId[conversation.assigned_to_graphene_id] || []
            const matchesGroup = selectedGroups.length === 0
                || selectedGroups.some(group => assigneeGroups.includes(group))

            return matchesInbox && matchesAssignment && matchesChannelType && matchesUnresponded && matchesGroup
        })
    }, [conversations, searchQuery, selectedInbox, assigneeParam, selectedChannelType, unrespondedOnly, groupParam])

    const formatTimestamp = (isoString) => {
        if (!isoString) return ''
        const m = moment(isoString)
        if (!m.isValid()) return ''
        return m.calendar(undefined, {
            sameDay: 'LT',
            lastDay: '[Yesterday] LT',
            lastWeek: 'MMM D',
            sameElse: 'MMM D'
        })
    }

    const formatStatusLabel = (status) => {
        if (!status) return 'Open'
        if (status.toUpperCase() === 'CLOSED') return 'Archived'
        return status.charAt(0).toUpperCase() + status.slice(1)
    }

    const onConversationArchived = (conversationId) => {
        // Redirect to the next open conversation after conversationId if any, otherwise, stay put
        // Get next item
        const currentIndex = filteredConversations.findIndex(conv => conv.graphene_id === conversationId)
        if (currentIndex === -1) return // if not found, do nothing
        const nextConv = filteredConversations[currentIndex + 1] || filteredConversations[currentIndex - 1]
        if (!nextConv) return // if not found, do nothing
        if (nextConv) {
            navigate({ pathname: `/comms/conversation/${nextConv.graphene_id}/`, search: searchParams.toString() }, { replace: true })
        }
    }
    
    // On first load, if no conversationId in URL and there are conversations, redirect to first conversation
    useEffect(() => {
    if (!conversationId && !loading && !loadingInboxes && filteredConversations.length > 0) {
        navigate({ pathname: `/comms/conversation/${filteredConversations[0].graphene_id}/`, search: searchParams.toString() }, { replace: true })
        }
    }, [conversationId, filteredConversations, loading, loadingInboxes, navigate])

    // Search narrows the list on every keystroke, but the jump waits for typing to settle.
    const [searchQueryForJump, setSearchQueryForJump] = useState(searchQuery)
    useEffect(() => {
        const timer = setTimeout(() => setSearchQueryForJump(searchQuery), 300)
        return () => clearTimeout(timer)
    }, [searchQuery])

    // Only a filter change arms the jump; the list reordering on a new message must not.
    useEffect(() => {
        if (!didBaselineFilters.current) {
            didBaselineFilters.current = true // first run only records the baseline, so deep links stay put
            return
        }
        if (skipNextJump.current) {
            skipNextJump.current = false // the default assignee, not a filter the user changed
            return
        }
        setPendingJump(true)
    }, [selectedStatus, selectedInboxId, selectedChannelType, assigneeParam, groupParam, unrespondedOnly, searchQueryForJump])

    // A filter change always reopens the first conversation of the newly filtered list.
    useEffect(() => {
        if (!pendingJump) return
        // Status is filtered server-side, so wait for the snapshot matching the requested status.
        if (loading || loadingInboxes || loadedStatus !== selectedStatus) return
        setPendingJump(false)
        const firstConversation = filteredConversations[0]
        navigate(
            firstConversation
                ? { pathname: `/comms/conversation/${firstConversation.graphene_id}/`, search: searchParams.toString() }
                : { pathname: "/comms", search: searchParams.toString() },
            { replace: true }
        )
    }, [pendingJump, loading, loadingInboxes, loadedStatus, selectedStatus, filteredConversations, navigate])

    if (loading) {
        return <Loader />
    }

    if (error) {
        return <div className="alert alert-danger">{error}</div>
    }

    if (!allInboxes || allInboxes.length === 0) {
        return <div className="alert alert-warning">No inboxes available.</div>
    }
    return (
        <Container fluid className='CommsContainer'>
            <div className="CommsBottomActions">
                <Link to="#" className="CommsNewConversationButton" title="Start new conversation" onClick={() => setShowNewModal(true)}>
                    <i className="fa fa-plus" aria-hidden="true"></i>
                </Link>
            </div>
            <CommsNewConversationModal
                isOpen={showNewModal}
                toggle={() => setShowNewModal(!showNewModal)}
                allInboxes={allInboxes}
                currentUser={currentUser}
            />
            <Row>
                {/* Left column for conversation list */}
                <Col xs="2" className="CommsColumn">
                    <div className="CommsFilterBar">
                        <div className="InboxDropdown">
                            <Dropdown isOpen={inboxOpen} toggle={() => setInboxOpen(!inboxOpen)}>
                            <DropdownToggle caret>{selectedInbox?.name || "All Inbox"}</DropdownToggle>
                            <DropdownMenu>
                                <DropdownItem header>Choose Inbox</DropdownItem>
                                <DropdownItem onClick={() => updateFilter("inbox", null)}> All </DropdownItem>
                                {allInboxes.map((inbox, idx) => (
                                <DropdownItem
                                    key={idx}
                                    onClick={() => updateFilter("inbox", inbox.id)}
                                >
                                    {inbox.name}
                                </DropdownItem>
                                ))}
                            </DropdownMenu>
                            </Dropdown>
                        </div>
                        <div className="StatusDropdown">
                            <Dropdown isOpen={channelOpen} toggle={() => setChannelOpen(!channelOpen)}>
                            <DropdownToggle caret>{selectedChannelType ? CHANNEL_TYPE_LABELS[selectedChannelType] || selectedChannelType : "All Channels"}</DropdownToggle>
                            <DropdownMenu>
                                <DropdownItem header>Filter by Channel</DropdownItem>
                                <DropdownItem onClick={() => updateFilter("channel_type", null)}>All Channels</DropdownItem>
                                {Object.entries(CHANNEL_TYPE_LABELS).map(([value, label]) => (
                                    <DropdownItem key={value} onClick={() => updateFilter("channel_type", value)}>
                                        {label}
                                    </DropdownItem>
                                ))}
                            </DropdownMenu>
                            </Dropdown>
                        </div>

                        <div className="RepFilterDropdown">
                            <AsyncSelect
                                isMulti
                                cacheOptions
                                defaultOptions={assigneeOptions}
                                loadOptions={loadAssigneeOptions}
                                value={selectedAssigneeIds.map(id => (
                                    assigneeOptions.find(o => o.value === id)
                                    || (selectedAssigneeLabels[id] ? { value: id, label: selectedAssigneeLabels[id] } : null)
                                    || { value: id, label: id }
                                ))}
                                onChange={(opts) => {
                                    setSelectedAssigneeLabels(prev => {
                                        const next = { ...prev }
                                        ;(opts || []).forEach(o => { next[o.value] = o.label })
                                        return next
                                    })
                                    updateFilter("assignee", (opts || []).map(o => o.value).join(",") || null)
                                }}
                                placeholder="Filter by assignee…"
                                classNamePrefix="react-select"
                                menuPortalTarget={document.body}
                                styles={{ menuPortal: base => ({ ...base, zIndex: 9999 }) }}
                            />
                        </div>

                        {groupOptions.length > 0 && (
                            <div className="RepFilterDropdown">
                                <Select
                                    isMulti
                                    options={groupOptions}
                                    value={selectedGroups.map(name => ({ value: name, label: name }))}
                                    onChange={(opts) => updateFilter("groups", (opts || []).map(o => o.value).join(",") || null)}
                                    placeholder="Filter by assignee group…"
                                    classNamePrefix="react-select"
                                    menuPortalTarget={document.body}
                                    styles={{ menuPortal: base => ({ ...base, zIndex: 9999 }) }}
                                />
                            </div>
                        )}

                        <div className="StatusDropdown">
                            <Dropdown isOpen={statusOpen} toggle={() => setStatusOpen(!statusOpen)}>
                            <DropdownToggle caret>{selectedStatus}</DropdownToggle>
                            <DropdownMenu>
                                <DropdownItem header>Filter by Status</DropdownItem>
                                {STATUSES.map((status, idx) => (
                                <DropdownItem
                                    key={idx}
                                    onClick={() => updateFilter("status", status)}
                                >
                                    {status}
                                </DropdownItem>
                                ))}
                            </DropdownMenu>
                            </Dropdown>
                        </div>

                        <FormGroup check className="UnrespondedFilter">
                            <Label check>
                                <Input
                                    type="checkbox"
                                    checked={unrespondedOnly}
                                    onChange={e => updateFilter("unresponded", e.target.checked ? "1" : null)}
                                />
                                Unresponded only
                            </Label>
                        </FormGroup>
                    </div>

                    {/* Search bar */}
                    <div>
                        <input
                            type="text"
                            placeholder="Search conversations..."
                            value={searchQuery}
                            onChange={e => updateFilter("search", e.target.value)}
                            className="form-control"
                        />
                    </div>
                    <ul className="CommsList">
                        {filteredConversations.length > 0 && filteredConversations.map((conversationItem, idx) => {
                            const isActive = conversationId === conversationItem.graphene_id
                            const isAssignedUnread = currentUserPk && conversationItem.assigned_to === currentUserPk && conversationItem.is_read_by_assigned_agent === false
                            const listItemClass = [isActive ? 'active' : 'inactive', isAssignedUnread ? 'assigned-unread' : ''].filter(Boolean).join(' ')
                            const subject = conversationItem.subject || conversationItem.contact_name || 'Conversation'
                            const contactLine = conversationItem.contact_phone || conversationItem.contact_email
                            const driverLine = conversationItem.driver_name
                            const statusLabel = formatStatusLabel(conversationItem.status)
                            const timestampLabel = formatTimestamp(conversationItem.last_message_at)
                            const statusClass = conversationItem.status ? `status-${conversationItem.status.toLowerCase()}` : 'status-open'

                            return (
                                <li className={listItemClass} key={idx}>
                                    <Link to={{ pathname: `/comms/conversation/${conversationItem.graphene_id}/`, search: searchParams.toString() }} className="CommsListLink">
                                        <div className="CommsListHeader">
                                            <span className="CommsListSubject" title={subject}>{subject}</span>
                                        </div>
                                        {(contactLine || driverLine) && (
                                            <div className="CommsListMeta">
                                                {contactLine && <span>{contactLine}</span>}
                                                {driverLine && <span>{driverLine}</span>}
                                            </div>
                                        )}
                                        <div className="CommsListFooter">
                                            <span className={`CommsListStatus ${statusClass}`}>{statusLabel}</span>
                                            {conversationItem?.assigned_to_username && <span className={`CommsListStatus`}>{conversationItem?.assigned_to_username}</span>}
                                            
                                            {isAssignedUnread && (
                                                    <span className="CommsListUnreadBadge" aria-label="Unread conversation">
                                                        Unread
                                                    </span>
                                                )}
                                            {timestampLabel && <span className="CommsListTime">{timestampLabel}</span>}
                                        </div>
                                    </Link>
                                </li>
                            )
                        })}
                    </ul>
                </Col>

                {/* Right column for displaying the CommsConversation */}
                <Col xs="10">
                    <AllUsersPresenceProvider>
                        <CommsConversation conversationIdFromParent={conversationId} onConversationArchived={onConversationArchived}  />
                    </AllUsersPresenceProvider>
                </Col>
            </Row>
        </Container>
    )
}
